import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * Starts a newly navigated page at the top.
 *
 * A browser resets scroll on a document load, but a client-side route change is not
 * one — so scroll position carried over from the page you left. Clicking "Get
 * started" from partway down the homepage opened /get-started already scrolled near
 * its foot, and the visitor had to scroll up to find the page they had just asked
 * for. That applied to every link on the site, and worst to the longest page: the
 * homepage is around seven screens tall, so its lower CTAs landed the furthest down.
 *
 * Three cases are deliberately left alone:
 *
 *   - Back and forward (`POP`). The browser restores the previous scroll offset,
 *     which is what the visitor expects and is better than anything we could do.
 *   - Any URL carrying a hash. The fragment names the position; overriding it would
 *     defeat the link.
 *   - The first render. On a fresh load or a reload the browser has already decided
 *     the offset — restoring mid-page on reload, or sitting at the top otherwise —
 *     and scrolling here would fight it.
 *
 * `behavior: 'instant'` is required rather than incidental: homepage.css sets
 * `scroll-behavior: smooth` on `html`, so a plain scrollTo would animate the whole
 * height of the page you are leaving instead of just starting at the top.
 */
export function ScrollToTopOnNavigate(): null {
  const { pathname, hash } = useLocation();
  const navigationType = useNavigationType();
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (navigationType === 'POP') return;
    if (hash) return;

    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  }, [pathname, hash, navigationType]);

  return null;
}
